import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { tap, catchError, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { API } from '../../API';
import { FEDERATION_SLUG } from '../../../../main';

export interface Federation {
  federation_id: number;
  federation_acornyme: string;
  federation_name: string;
}

export interface AuthResponse {
  success: boolean;
  user: User;
  federation?: Federation;
}

export interface User {
  id: number;
  username: string;
  roles: string[];
  user_object_type: string;
  user_object_id?: number;
  permissions?: Record<string, string[]>;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly baseUrl = environment.apiUrl;
  private userDataSignal = signal<User | null>(null);
  private federationDataSignal = signal<Federation | null>(null);
  private currentUserSubject = new BehaviorSubject<User | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();

  constructor(private httpClient: HttpClient) {}

  restoreSession(): Promise<void> {
    return new Promise((resolve) => {
      this.httpClient.get<AuthResponse>(
        `${this.baseUrl}${API.AUTH.ME}`,
        { withCredentials: true }
      ).subscribe({
        next: (response) => {
          if (response.success && response.user) {
            this.setUser(response.user);
            if (response.federation) this.setFederation(response.federation);
            this.currentUserSubject.next(response.user);
          }
          resolve();
        },
        error: () => resolve(),
      });
    });
  }

  get federationSlug(): string | null { return FEDERATION_SLUG; }

  login(username: string, password: string): Observable<AuthResponse> {
    return this.httpClient.post<AuthResponse>(
      `${this.baseUrl}${API.AUTH.LOGIN}`,
      { username, password, federation_slug: this.federationSlug, user_object_type: 'FEDE' },
      { withCredentials: true }
    ).pipe(
      switchMap(loginResponse => {
        if (!loginResponse.success) return of(loginResponse);
        // Login succeeded — cookie is set. Fetch permissions from /auth/me
        return this.httpClient.get<AuthResponse>(
          `${this.baseUrl}${API.AUTH.ME}`,
          { withCredentials: true }
        ).pipe(
          tap(meResponse => {
            if (meResponse.success) {
              this.setUser(meResponse.user);
              if (meResponse.federation) this.setFederation(meResponse.federation);
              this.currentUserSubject.next(meResponse.user);
            }
          }),
          catchError(() => {
            this.setUser(loginResponse.user);
            if (loginResponse.federation) this.setFederation(loginResponse.federation);
            this.currentUserSubject.next(loginResponse.user);
            return of(loginResponse);
          })
        );
      })
    );
  }

  logout(): Observable<any> {
    return this.httpClient.post(
      `${this.baseUrl}${API.AUTH.LOGOUT}`,
      {},
      { withCredentials: true }
    ).pipe(
      tap(() => this.clearAuth()),
      catchError((_error: any) => {
        this.clearAuth();
        return new Observable(observer => observer.complete());
      })
    );
  }

  refreshToken(): Observable<any> {
    return this.httpClient.post(`${this.baseUrl}${API.AUTH.REFRESH}`, {}, { withCredentials: true });
  }

  clearLocalAuth(): void {
    this.userDataSignal.set(null);
    this.federationDataSignal.set(null);
    this.currentUserSubject.next(null);
  }

  private clearAuth(): void {
    this.clearLocalAuth();
  }

  setUser(user: User): void {
    this.userDataSignal.set(user);
  }

  getUser(): User | null {
    return this.userDataSignal();
  }

  isAuthenticated(): boolean {
    return this.currentUserSubject.getValue() !== null;
  }

  isFederation(): boolean {
    const user = this.getUser();
    return user?.user_object_type === 'FEDE';
  }

  setFederation(federation: Federation): void {
    this.federationDataSignal.set(federation);
  }

  getFederation(): Federation | null {
    return this.federationDataSignal();
  }
}
